// frontend/src/pages/UserProfilePage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import ProductCard from '../components/ProductCard';
import { defaultAvatars } from '../services/avatars';

const defaultAvatar = defaultAvatars[0];

// Componente para mostrar estrellas (Solo lectura)
const ReadOnlyStars = ({ rating }) => {
  // Convertimos a número por seguridad
  const numRating = Number(rating) || 0;
  const roundedRating = Math.round(numRating);

  return (
    <div>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className="fs-5"
          style={{ color: star <= roundedRating ? '#ffc107' : '#e4e5e9' }}
        >
          &#9733;
        </span>
      ))}
    </div>
  );
};

function UserProfilePage() {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { id } = useParams();

  // Estado derivado para estadísticas
  const [stats, setStats] = useState({ avg: 0, total: 0 });

  useEffect(() => {
    const fetchUserProfile = async () => {
      setUser(null);
      setItems([]);
      setLoading(true);
      setError('');
      try {
        const [userRes, itemsRes] = await Promise.all([
            api.get(`/users/${id}`),
            api.get(`/users/${id}/items`, { params: { disponible: true } })
        ]);

        const userData = userRes.data;
        setUser(userData);
        setItems(itemsRes.data);

        // --- CÃLCULO DE PROMEDIO ROBUSTO ---
        if (userData.valoraciones_recibidas && userData.valoraciones_recibidas.length > 0) {
            const totalVal = userData.valoraciones_recibidas.length;
            const suma = userData.valoraciones_recibidas.reduce((acc, curr) => {
                return acc + (Number(curr.calificacion) || 0);
            }, 0);
            setStats({
                avg: (suma / totalVal),
                total: totalVal
            });
        } else {
            setStats({ avg: 0, total: 0 });
        }
        // -----------------------------------

      } catch (err) {
        console.error("Error cargando perfil:", err);
        setError('No se pudo cargar el perfil del usuario.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [id]);


  if (loading) return <div className="text-center mt-5">Cargando perfil...</div>;
  if (error) return <div className="alert alert-danger mt-4">{error}</div>;
  if (!user) return <div className="alert alert-warning mt-4">Usuario no encontrado.</div>;

  const userAvatar = user.avatar_url || defaultAvatar;

  return (
    <div className="row mt-4">
      {/* Columna Izquierda: Info Usuario y Estadísticas */}
      <div className="col-md-4 mb-4">
        <div className="card shadow-sm border-0 text-center p-4">
            <img
                src={userAvatar}
                alt={user.username}
                className="img-fluid rounded-circle mx-auto mb-3 border"
                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
            />
            <h3 className="card-title">{user.nombre_completo || user.username}</h3>
            <p className="text-muted">@{user.username}</p>

            {/* Bloque de Calificación */}
            <div className="my-3 p-3 bg-light rounded border">
                <h1 className="fw-bold mb-0 display-4">{stats.avg.toFixed(1)}</h1>
                <div className="d-flex justify-content-center my-2">
                    <ReadOnlyStars rating={stats.avg} />
                </div>
                <small className="text-muted text-uppercase fw-bold" style={{fontSize: '0.8rem'}}>
                    {stats.total} {stats.total === 1 ? 'Valoración' : 'Valoraciones'}
                </small>
            </div>

            <hr />
            <div className="text-start px-2">
                {user.facultad && (
                    <p className="mb-2">
                        <i className="bi bi-mortarboard-fill me-2 text-primary"></i>
                        {user.facultad.nombre}
                    </p>
                )}
                {user.carrera && (
                    <p className="mb-2">
                        <i className="bi bi-book-fill me-2 text-secondary"></i>
                        {user.carrera}
                    </p>
                )}
                <p className="mb-2">
                    <i className="bi bi-calendar-event me-2 text-muted"></i>
                    Se unió en {new Date(user.fecha_registro).toLocaleDateString()}
                </p>
            </div>
            {user.bio && (
                <div className="mt-4 fst-italic text-muted border-start border-3 ps-3">
                    "{user.bio}"
                </div>
            )}
        </div>

        {/* Lista de Reseñas */}
        <div className="card shadow-sm border-0 mt-3">
            <div className="card-header bg-white fw-bold border-bottom-0 pt-3">
                Últimas Reseñas
            </div>
            <div className="card-body p-0">
                {user.valoraciones_recibidas && user.valoraciones_recibidas.length > 0 ? (
                    <div className="list-group list-group-flush">
                        {user.valoraciones_recibidas.map((val, idx) => {
                            // Fallback para nombre de usuario
                            const nombreComprador = val.comprador ? val.comprador.username : 'Usuario Anónimo';

                            return (
                                <div key={val.id || idx} className="list-group-item border-0 border-bottom py-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small className="fw-bold text-dark">{nombreComprador}</small>
                                        <div className="d-flex align-items-center">
                                            <span className="text-warning fs-6 me-1"></span>
                                            <span className="fw-bold small">{val.calificacion}</span>
                                        </div>
                                    </div>
                                    {val.comentario ? (
                                        <p className="mb-0 small text-secondary">"{val.comentario}"</p>
                                    ) : (
                                        <p className="mb-0 small text-muted fst-italic">Sin comentario</p>
                                    )}
                                    <div className="text-end mt-1">
                                        <small className="text-muted" style={{fontSize: '0.7rem'}}>
                                            {val.fecha_creacion ? new Date(val.fecha_creacion).toLocaleDateString() : ''}
                                        </small>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center p-4">
                        <i className="bi bi-chat-square-text text-muted fs-2 mb-2 d-block"></i>
                        <p className="text-muted small mb-0">Este usuario aún no tiene reseñas.</p>
                    </div>
                )}
            </div>
        </div>
      </div>

      {/* Columna Derecha: Productos */}
      <div className="col-md-8">
        <div className="d-flex justify-content-between align-items-center mb-3">
             <h4 className="mb-0">Publicaciones</h4>
             <span className="badge bg-secondary">{items.length} Activas</span>
        </div>

        <hr />

        {Array.isArray(items) && items.length > 0 ? (
          <div className="row row-cols-1 row-cols-md-2 g-4">
            {items.map((item) => (
              item ? (
                  <div className="col" key={item.id}>
                    <ProductCard item={item} />
                  </div>
              ) : null
            ))}
          </div>
        ) : (
          <div className="alert alert-light text-center border p-5">
              <i className="bi bi-box-seam text-muted fs-1 mb-3 d-block"></i>
              <p className="mb-0">Este usuario no tiene publicaciones activas por el momento.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfilePage;
